// Despite the name of the file, this class actually represents the settings screen where you can
// edit default rate cards for a team.

import React from 'react'

import {
  lightTheme,
  RateCardForm,
  RateCardDefaultSettings,
  ThemeProvider,
  Grid
} from '@rodeo-dev/legos'
import app from '../../../app.jsx'
import eventfilters from '../../../lib/eventfilters.jsx'
import ContentMessage from '../../../common/common/content_message/content_message.jsx'
import currency from '../../../lib/currency.jsx'
import BaseSettings from '../../page/base_setting/base_setting.jsx'
import IntlUtil from '../../../../ui/lib/intlutil.jsx'

import {
  saveRateCards,
  prepareRateCardsForStorage,
  getRateCardsByType,
  updateDefaultRateMarkup
} from './ratecardCrud'

import { TIME_RATE_CARD, EXPENSE_RATE_CARD } from '../../../../shared/constants'
import Util from '../../../lib/util.jsx'

class PagePayment extends BaseSettings {
  constructor (props) {
    super(props, 'setting-payment', 'setting-user', 'setting-payment')

    this.state = {
      isLoading: true,
      saving: false,
      dataDate: null
    }

    this.data = {
      rateCards: null
    }

    this._references = {
      pageHeader: React.createRef()
    }

    this._bUnMounted = false
    this._eventNames = ['created', 'updated', 'removed', 'patched']
    this._ratecardName = 'ratecard'
  }

  componentDidMount () {
    this._getData()
    this._initEvents()
  }

  componentWillUnmount () {
    this._bUnMounted = true
    this._releaseEvents()
  }

  _onChangePage (pageIdent) {
    app.changePage(pageIdent)
  }

  _onSubmit (rateCardsFromForm, type) {
    this.setState({ saving: true })
    const { rateCards, rateCardIds } = prepareRateCardsForStorage(
      getRateCardsByType(this.data.rateCards, type),
      rateCardsFromForm,
      type,
      app.getTeamId()
    )
    saveRateCards(
      app.service('/api/ratecard'),
      app.getTeamId(),
      rateCards,
      rateCardIds,
      err => {
        this.state.saving = false
        if (err) {
          this._onError()
        } else {
          this._getData()
          this._onSuccess()
        }
      }
    )
  }

  _getData () {
    this._getPageDataWithPromises()
      .then(this._getDataPayment.bind(this))
      .finally(this._onDataReceived.bind(this))
  }

  _getDataPayment (callback) {
    app.debug('PagePayment: _getDataPayment [start]')

    const params = {}
    this.data.rateCards = []
    return app
      .service('/api/ratecard')
      .find(params)
      .then(result => {
        this.data.rateCards = result.data
      })
  }

  _onDataReceived (err) {
    if (this._bUnMounted) return

    if (err) {
      app.debug('PagePayment: _onDataReceived Error', err)
    } else {
      app.debug('PagePayment: _onDataReceived [done]')
    }
    this.setState({ isLoading: !!err, dataDate: new Date(), saving: false })
  }

  renderContent () {
    return (
      <div className='page-payment'>
        {this._renderMessage()}
        {this._renderForm()}
      </div>
    )
  }

  _renderMessage () {
    return this.state.loading ? (
      <ContentMessage
        message='setting.rateCard.msgOnLoad'
        info=''
        showAddPrefix={false}
        showAdd={false}
      />
    ) : (
      ''
    )
  }

  _renderForm () {
    const {
      decimalSeparator,
      thousandSeparator
    } = Util.getNumberInputSeparators(app.teamCountry)
    const inputsPropsObject = {
      markupInputProps: {
        decimalSeparator,
        thousandSeparator
      },
      rateInputProps: {
        fixedDecimalScale: true,
        decimalSeparator,
        thousandSeparator
      }
    }
    return this.state.loading ? (
      ''
    ) : (
      <ThemeProvider theme={lightTheme}>
        <div className='ratecard-form'>
          <Grid container spacing={4}>
            <Grid item sm={6}>
              <RateCardDefaultSettings
                title={IntlUtil.convertMessage('ratecard.form.defaultSettings')}
                onSave={({ rateMarkup }) => {
                  updateDefaultRateMarkup(app, app.getTeamId(), rateMarkup)
                    .then(() => this._onSuccess())
                    .catch(err => this._onError(err))
                }}
                currencySymbol={currency.symbol()}
                rateMarkup={app.getDefaulRateMarkup()}
                type='global'
                handleRateMarkupChangeOverride={val => val || 0}
                {...inputsPropsObject}
              />
            </Grid>
            <Grid item sm={12}>
              <Grid container spacing={4}>
                <Grid item sm={6}>
                  <RateCardForm
                    rates={getRateCardsByType(
                      this.data.rateCards,
                      TIME_RATE_CARD
                    )}
                    type={TIME_RATE_CARD}
                    title={IntlUtil.convertMessage('ratecard.form.titleTime')}
                    onSave={newRates => {
                      this._onSubmit(newRates, TIME_RATE_CARD)
                    }}
                    currencySymbol={currency.symbol()}
                    {...inputsPropsObject}
                  />
                </Grid>
                <Grid item sm={6}>
                  <RateCardForm
                    rates={getRateCardsByType(
                      this.data.rateCards,
                      EXPENSE_RATE_CARD
                    )}
                    type={EXPENSE_RATE_CARD}
                    title={IntlUtil.convertMessage(
                      'ratecard.form.titleExpense'
                    )}
                    onSave={newRates => {
                      this._onSubmit(newRates, EXPENSE_RATE_CARD)
                    }}
                    currencySymbol={currency.symbol()}
                    {...inputsPropsObject}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </ThemeProvider>
    )
  }

  _onSuccess () {
    if (this._references.pageHeader.current) {
      this._references.pageHeader.current.showSuccess(
        'setting.rateCard.msgSuccessTitle',
        'setting.rateCard.msgSuccessContent'
      )
    }
  }

  _onError () {
    if (this._references.pageHeader.current) {
      this._references.pageHeader.current.showError(
        'setting.rateCard.msgErrorTitle',
        'setting.rateCard.msgErrorInfo'
      )
    }
  }

  /* Realtime Events */
  _initEvents () {
    const ratecardService = app.service('/api/ratecard')

    this._eventNames.forEach(event => {
      const eventHandlerName = this._ratecardName + event
      this._eventHandlers[eventHandlerName] = this._eventReload.bind(
        this,
        this._ratecardName,
        event
      )
      ratecardService.on(event, this._eventHandlers[eventHandlerName])
    })
  }

  _releaseEvents () {
    const ratecardService = app.service('/api/ratecard')

    this._eventNames.forEach(event => {
      const eventHandlerName = this._ratecardName + event
      ratecardService.off('event', this._eventHandlers[eventHandlerName])
      this._eventHandlers[eventHandlerName] = null
    })
  }

  _eventReload (objectName, eventName, eventData) {
    if (!this.state.saving && eventfilters.teamFilter(eventData)) {
      app.debug(`-- Event - PagePayment - ${objectName} - ${eventName} --`)
      this._getData()
    }
  }
}

export default PagePayment
